import LISTINGSIMAGE from "../assets/Overview.jpg";
import PropTypes from "prop-types";
import React, { useMemo, useEffect, useState } from "react";
import { isPdfUrl } from "../Services/helper";
import { formatDateRangeInTZ } from "../Services/datesHelper";
import { renderPdfFirstPageToDataUrl } from "../Services/pdfToImage";
import { getCachedPdfData, cachePdfData, cachePdfUrl } from "../Services/pdfCache";
import mediaType from "../Constants/mediaType";

const TEMPLATE_A_HORIZONTAL = `
w-full max-w-[202rem]
sm:w-[24rem]
md:w-[20rem]
lg:w-[18rem]
xl:w-[18rem]
2xl:w-[17rem]
2xl:h-[19rem]
h-56`;

const TEMPLATE_B_HORIZONTAL = `w-full max-w-[202rem]
                 sm:w-[24rem]
                 md:w-[20rem]
                 lg:w-[18rem]
                 xl:w-[18rem]
                 2xl:w-[17rem]
                 h-56`;
//  2xl:h-[17rem]  

const TEMPLATE_A_VERTICAL = `
w-full max-w-[202rem]
              sm:w-[24rem]
              md:w-[20rem]
              lg:w-[18rem]
              lg:h-[25rem]
              xl:w-[15rem]
              xl:h-[25rem]
              2xl:w-[20rem]
              2xl:h-[25rem]
              h-56
`;

const TEMPLATE_B_VERTICAL = `
w-full max-w-[202rem]
              sm:w-[24rem]
              md:w-[20rem]
              lg:w-[18rem]
              lg:h-[25rem]
              xl:w-[15rem]
              xl:h-[25rem]
              2xl:w-[20rem]
              2xl:h-[25rem]
              h-56`;

const TEMP_MAP = {
  A: {
    horizontal: TEMPLATE_A_HORIZONTAL,
    vertical: TEMPLATE_A_VERTICAL,
  },
  B: {
    horizontal: TEMPLATE_B_HORIZONTAL,
    vertical: TEMPLATE_B_VERTICAL,
  },
};


const Loader = () => <div className="w-8 h-8 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />


function ListingsCardTerminal({
  listing,
  terminalView,
  iFrame = false,
  isPopUp,
  setListingPopup,
  orientation,
  type,
  className = '',
}) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [pdfThumb, setPdfThumb] = useState("");
  const [pdfTotalPages, setPdfTotalPages] = useState(0);
  const [isConvertingPdfToImage, setIsConvertingPdfToImage] = useState(false);

  const validMedia = (listing.media || []).filter(
    (item) => item.signedUrl !== null
  );
  const pdfMedia = validMedia.find((item) => item.mediaType === mediaType.PDF);

  const handleListingClick = () => {
    if (isPopUp) {
      setListingPopup(listing);
      return;
    }
  };

  const dateRange = useMemo(() => formatDateRangeInTZ(listing.startDate, listing.endDate), [listing])

  const templateCardClassName = useMemo(() =>
    TEMP_MAP[type]?.[orientation]
    , [type, orientation])

  // TODO: HANDLER
  if (!type) return null;

  // Load PDF thumbnail from cache or render once and cache
  useEffect(() => {
    let isMounted = true;
    if (pdfMedia) {
      setIsConvertingPdfToImage(true);
      const firstSignedUrl = listing?.media?.[0]?.signedUrl;
      if (!firstSignedUrl || !isPdfUrl(firstSignedUrl)) {
        setPdfThumb("");
        setPdfTotalPages(0);
        return;
      }
      const listingId = listing?.id ?? listing?.listingId ?? listing?._id;
      cachePdfUrl(listingId, firstSignedUrl);
      const cached = getCachedPdfData(listingId,'thumbnail');
      if (cached && cached.images && cached.images.length > 0) {
        setPdfThumb(cached.images[0]);
        setPdfTotalPages(cached.totalPages || cached.images.length);
        setIsConvertingPdfToImage(false);
        return;
      }
      (async () => {
        try {
          setIsConvertingPdfToImage(true);
          const result = await renderPdfFirstPageToDataUrl(firstSignedUrl);
          if (!isMounted) return;
          setPdfThumb(result.firstPageImage || "");
          setPdfTotalPages(result.totalPages || result.firstPageImage||'');
          cachePdfData(listingId, firstSignedUrl, [result.firstPageImage], result.totalPages,'thumbnail');
        } catch (e) {
          // ignore; fallback image will be shown
          console.error(e);
        }finally{
          setIsConvertingPdfToImage(false);
        }
      })();
    }
    return () => {
      isMounted = false;
    };
  }, [listing]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        handleListingClick();
      }}
      className={`
          bg-white rounded-xl shadow-md overflow-hidden
          transition-transform duration-300 hover:-translate-y-1
          cursor-pointer flex flex-col
          ${templateCardClassName}
          ${className}
        `}
    >
      {/* Image/PDF */}
      <div
        className={`relative w-full ${orientation !== "horizontal" ? "h-[16rem]" : "h-36"
          }`}
      >
        {
        isConvertingPdfToImage ?  <div className="w-full h-full flex items-center justify-center">
                  <Loader />
                </div> :
        listing.media?.[0]?.signedUrl && isPdfUrl(listing.media[0].signedUrl) ? (
          <img
            alt="PDF Thumbnail"
            className="object-cover w-full h-full"
            src={pdfThumb || LISTINGSIMAGE}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = LISTINGSIMAGE;
            }}
          />
        ) : (
          <img
            alt="Listing"
            className="object-cover w-full h-full"
            src={listing.media?.[0]?.signedUrl || LISTINGSIMAGE}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = LISTINGSIMAGE;
            }}
          />
        )}
        <svg
          className="absolute bottom-0 left-0 w-full text-white"
          viewBox="0 0 1440 320"
        >
          <path
            fill="currentColor"
            d="M0,256 C480,400 960,112 1440,256L1440,320L0,320Z"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="pl-3 pr-3 flex flex-col flex-grow">
        <h2 className="text-gray-800 font-semibold text-base sm:text-lg truncate">
          {listing.title}
        </h2>

        <p
          className="text-gray-600 text-sm mt-1 leading-snug overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
          dangerouslySetInnerHTML={{ __html: listing.description }}
        />


        {dateRange && (
          <p className="text-gray-500 text-xs mt-1">
            {dateRange}
          </p>
        )}
      </div>
    </div>
  );
}

ListingsCardTerminal.propTypes = {
  listing: PropTypes.object.isRequired,
  terminalView: PropTypes.bool,
  iFrame: PropTypes.bool,
  isPopUp: PropTypes.bool,
  setListingPopup: PropTypes.func,
  orientation: PropTypes.string,
};

export default ListingsCardTerminal;
